import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { DoctorQuestion, MedicalAppointment, Medication, SymptomEntry } from '@/types/api';
import type { MedicationDoseEvent } from '@/services/api';
import i18n from 'i18next';
import { csvCell } from '@/lib/csvCell';
import { parseDateOnly, dateOnlyIntl } from '@/lib/safe-date';
import type { AnyTFunction } from '@/i18n/msg';
import { labelFor } from '@/i18n/labels';
import { bcp47For } from '@/i18n/negotiate';


// ── Appointment type tokens ───────────────────────────────────────────────
//
// Each token bundles a tinted band background, a deeper text accent, and a
// dot color used in the type-pill. The design uses a 9-type palette; we
// match it 1:1.

export interface ApptTypeTokens {
  band: string;       // tw class for the left band tint
  pill: string;       // tw class for the type-pill (bg + text)
  dot: string;        // tw class for the dot in the type-pill
  accent: string;     // tw class for the deeper accent (date stamp)
}

export const APPT_TYPE_TOKENS: Record<MedicalAppointment['appointment_type'], ApptTypeTokens> = {
  // Appointment types are CATEGORIES — none of them is a state, and none
  // borrows a status hue. A scheduled surgery is not 'broken'; radiation is
  // not 'waiting'. The four categorical hues go to the four events a family
  // tracks most; the rest are neutral and carry their meaning in the label.
  // `oncology_consult` keeps indigo because it is the primary care thread.
  chemotherapy:     { band: 'bg-cat-violet', pill: 'bg-cat-violet text-cat-violet-fg', dot: 'bg-cat-violet-fg', accent: 'text-cat-violet-fg' },
  radiation:        { band: 'bg-cat-magenta', pill: 'bg-cat-magenta text-cat-magenta-fg', dot: 'bg-cat-magenta-fg', accent: 'text-cat-magenta-fg' },
  scan:             { band: 'bg-cat-sky', pill: 'bg-cat-sky text-cat-sky-fg', dot: 'bg-cat-sky-fg', accent: 'text-cat-sky-fg' },
  lab:              { band: 'bg-chip-neutral', pill: 'bg-chip-neutral text-chip-neutral-fg', dot: 'bg-chip-neutral-fg', accent: 'text-chip-neutral-fg' },
  oncology_consult: { band: 'bg-stevens-indigo/5', pill: 'bg-stevens-indigo/10 text-stevens-indigo',  dot: 'bg-stevens-indigo',    accent: 'text-stevens-indigo'    },
  surgery:          { band: 'bg-cat-teal', pill: 'bg-cat-teal text-cat-teal-fg', dot: 'bg-cat-teal-fg', accent: 'text-cat-teal-fg' },
  physical_therapy: { band: 'bg-chip-neutral', pill: 'bg-chip-neutral text-chip-neutral-fg', dot: 'bg-chip-neutral-fg', accent: 'text-chip-neutral-fg' },
  social_worker:    { band: 'bg-chip-neutral', pill: 'bg-chip-neutral text-chip-neutral-fg', dot: 'bg-chip-neutral-fg', accent: 'text-chip-neutral-fg' },
  other:            { band: 'bg-chip-neutral', pill: 'bg-chip-neutral text-chip-neutral-fg', dot: 'bg-chip-neutral-fg', accent: 'text-chip-neutral-fg' },
};

// ── Medication helpers ────────────────────────────────────────────────────

// Label maps are KEY maps resolved at render with `labelFor` (#2109 R3-B4).
// No staff file imports them, so none keeps an English compatibility getter.

export type DoseSlot = 'morning' | 'midday' | 'evening' | 'bedtime';

export const SLOT_LABEL_KEY = {
  morning: 'health:slot.morning',
  midday:  'health:slot.midday',
  evening: 'health:slot.evening',
  bedtime: 'health:slot.bedtime',
} as const satisfies Record<DoseSlot, string>;

export const TIME_OF_DAY_SLOTS = [
  { key: 'morning', labelKey: SLOT_LABEL_KEY.morning },
  { key: 'midday',  labelKey: SLOT_LABEL_KEY.midday },
  { key: 'evening', labelKey: SLOT_LABEL_KEY.evening },
  { key: 'bedtime', labelKey: SLOT_LABEL_KEY.bedtime },
] as const;

export const ALL_DOSE_SLOTS: readonly DoseSlot[] = ['morning', 'midday', 'evening', 'bedtime'];

export const MED_FREQUENCY_LABEL_KEY = {
  once_daily:        'health:frequency.once_daily',
  twice_daily:       'health:frequency.twice_daily',
  three_times_daily: 'health:frequency.three_times_daily',
  every_x_hours:     'health:frequency.every_x_hours',
  weekly:            'health:frequency.weekly',
  as_needed:         'health:frequency.as_needed',
  other:             'health:frequency.other',
} as const satisfies Record<Medication['frequency'], string>;

export const ALL_MED_FREQUENCIES: readonly Medication['frequency'][] = [
  'once_daily', 'twice_daily', 'three_times_daily', 'every_x_hours', 'weekly', 'as_needed', 'other',
];

/** The medication form as a label, not the raw enum (R3-B22). */
export const MED_FORM_LABEL_KEY = {
  pill:      'health:form.pill',
  liquid:    'health:form.liquid',
  injection: 'health:form.injection',
  patch:     'health:form.patch',
  other:     'health:form.other',
} as const satisfies Record<Medication['form'], string>;

export const ALL_MED_FORMS: readonly Medication['form'][] = ['pill', 'liquid', 'injection', 'patch', 'other'];

const REFILL_WARNING_DAYS = 7;

/**
 * "today" · "tomorrow" · "in 3 days" — a whole-day offset through
 * `Intl.RelativeTimeFormat` with `numeric: 'auto'` (R3-B5), in the active
 * language. `formatRelative` (`i18n/intl.ts`) picks its unit from the
 * millisecond gap, so a zero-day offset would read "now"; a calendar-day
 * count needs the day unit named.
 */
export function formatDaysFromToday(days: number, locale?: string): string {
  const code = locale ?? i18n.resolvedLanguage ?? i18n.language ?? 'en';
  return new Intl.RelativeTimeFormat(bcp47For(code), { numeric: 'auto' }).format(days, 'day');
}

export function isRefillSoon(med: Medication, now = new Date()): boolean {
  if (!med.refill_reminder || !med.refill_date) return false;
  // `refill_date` is date-only (#1308) — compare calendar days, not the stored
  // UTC-midnight instant, or the warning window opens a day early.
  const d = parseDateOnly(med.refill_date);
  if (!d) return false;
  const days = differenceInCalendarDays(d, now);
  return days >= 0 && days <= REFILL_WARNING_DAYS;
}

export function reactionsForMedication(symptoms: SymptomEntry[], medId: string): number {
  return symptoms.filter((s) => s.linked_medication_id === medId).length;
}

// ── Appointment helpers ───────────────────────────────────────────────────

const PREP_WARNING_HOURS = 48;

export function isPrepSoon(appt: MedicalAppointment, now = new Date()): boolean {
  if (!appt.prep_required) return false;
  if (appt.status !== 'scheduled') return false;
  const ms = parseISO(appt.date_time).getTime() - now.getTime();
  return ms >= 0 && ms <= PREP_WARNING_HOURS * 3_600_000;
}

export function isToday(iso: string, now = new Date()): boolean {
  const d = parseISO(iso);
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth()    === now.getMonth() &&
    d.getDate()     === now.getDate()
  );
}

export function daysUntil(iso: string, now = new Date()): number {
  return differenceInCalendarDays(parseISO(iso), now);
}

export function isUpcomingAppt(appt: MedicalAppointment): boolean {
  if (appt.status !== 'scheduled') return false;
  return parseISO(appt.date_time).getTime() >= startOfTodayMs();
}

function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ── Question helpers ──────────────────────────────────────────────────────

export const QUESTION_CATEGORY_LABEL_KEY = {
  treatment:    'health:questionCategory.treatment',
  side_effects: 'health:questionCategory.side_effects',
  medications:  'health:questionCategory.medications',
  test_results: 'health:questionCategory.test_results',
  next_steps:   'health:questionCategory.next_steps',
  other:        'health:questionCategory.other',
} as const satisfies Record<DoctorQuestion['category'], string>;

export const ALL_QUESTION_CATEGORIES: readonly DoctorQuestion['category'][] = [
  'treatment', 'side_effects', 'medications', 'test_results', 'next_steps', 'other',
];

export const QUESTION_PRIORITY_LABEL_KEY = {
  this_visit:    'health:questionPriority.this_visit',
  can_wait:      'health:questionPriority.can_wait',
  already_asked: 'health:questionPriority.already_asked',
} as const satisfies Record<DoctorQuestion['priority'], string>;

export const ALL_QUESTION_PRIORITIES: readonly DoctorQuestion['priority'][] = ['this_visit', 'can_wait', 'already_asked'];

// ── Symptom helpers ───────────────────────────────────────────────────────

export interface SymptomSeverityTokens {
  pill: string;       // tw class for the severity pill
  marker: string;     // tw class for the timeline marker dot
  rail: string;       // tw class for the timeline left rail
}

export const SYMPTOM_SEVERITY_TOKENS: Record<SymptomEntry['severity'], SymptomSeverityTokens> = {
  mild:     { pill: 'bg-status-ok text-status-ok-fg', marker: 'bg-status-ok-fg', rail: 'bg-status-ok' },
  moderate: { pill: 'bg-status-warn text-status-warn-fg',     marker: 'bg-status-warn-fg',   rail: 'bg-status-warn' },
  severe:   { pill: 'bg-status-danger text-status-danger-fg',       marker: 'bg-status-danger-fg',    rail: 'bg-status-danger' },
};

export const SYMPTOM_SEVERITY_LABEL_KEY = {
  mild:     'health:severity.mild',
  moderate: 'health:severity.moderate',
  severe:   'health:severity.severe',
} as const satisfies Record<SymptomEntry['severity'], string>;

export const ALL_SEVERITIES: readonly SymptomEntry['severity'][] = ['mild', 'moderate', 'severe'];

export const SYMPTOM_TYPE_LABEL_KEY = {
  symptom:             'health:symptomType.symptom',
  medication_reaction: 'health:symptomType.medication_reaction',
  treatment_reaction:  'health:symptomType.treatment_reaction',
  improvement:         'health:symptomType.improvement',
  other:               'health:symptomType.other',
} as const satisfies Record<SymptomEntry['type'], string>;

export const ALL_SYMPTOM_TYPES: readonly SymptomEntry['type'][] = [
  'symptom', 'medication_reaction', 'treatment_reaction', 'improvement', 'other',
];

// "Severe + unreported in past 14 days" drives the red banner.
const RECENT_SYMPTOM_DAYS = 14;

export function hasUnreportedSevere(symptoms: SymptomEntry[], now = new Date()): boolean {
  return symptoms.some((s) => {
    if (s.severity !== 'severe') return false;
    if (s.reported_to_doctor) return false;
    // `s.date` is date-only (#1308) — compare the day, not the UTC-midnight
    // instant, or the window slides by one for every reader west of UTC.
    const d = parseDateOnly(s.date);
    if (!d) return false;
    const days = differenceInCalendarDays(now, d);
    return days >= 0 && days <= RECENT_SYMPTOM_DAYS;
  });
}

// ── Section types ─────────────────────────────────────────────────────────

// Three sections since the 2026-09-09 declutter. Appointments live on the
// calendar now — it already listed them, and a second home for the same
// records was the duplication Victor's walkthrough named. `/medical?tab=
// appointments` redirects there (HealthPage).
export type MedicalSection = 'medications' | 'questions' | 'symptoms';

// "Questions", not "Doctor questions" — one label per destination (#624);
// the epic's review settled the naming in #848.
export const SECTION_LABEL_KEY = {
  medications: 'health:section.medications',
  questions:   'health:section.questions',
  symptoms:    'health:section.symptoms',
} as const satisfies Record<MedicalSection, string>;

export const MEDICAL_SECTIONS: { value: MedicalSection; labelKey: (typeof SECTION_LABEL_KEY)[MedicalSection] }[] = [
  { value: 'medications',  labelKey: SECTION_LABEL_KEY.medications },
  { value: 'questions',    labelKey: SECTION_LABEL_KEY.questions },
  { value: 'symptoms',     labelKey: SECTION_LABEL_KEY.symptoms },
];

export function isMedicalSection(s: string | null | undefined): s is MedicalSection {
  return s === 'medications' || s === 'questions' || s === 'symptoms';
}

const SECTION_KEY = 'trf:medical-tab';

export function loadMedicalSection(fallback: MedicalSection = 'medications'): MedicalSection {
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(SECTION_KEY);
  return isMedicalSection(raw) ? raw : fallback;
}

export function persistMedicalSection(s: MedicalSection): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SECTION_KEY, s);
}

// ── Toolbar filters ───────────────────────────────────────────────────────
//
// One object for all three sections so switching tabs keeps each section's
// choice. HealthPage owns the state; MedicalToolbar renders and edits it.

export type MedScope = 'active' | 'inactive';
export type QuestionScope = 'pending' | 'answered';
export type SymptomScope = 'all' | 'notable' | 'reactions';
export type SeverityScope = 'all' | SymptomEntry['severity'];

export interface MedicalFilters {
  medScope: MedScope;
  qScope: QuestionScope;
  symScope: SymptomScope;
  severity: SeverityScope;
}

export const DEFAULT_FILTERS: MedicalFilters = {
  medScope: 'active',
  qScope: 'pending',
  symScope: 'all',
  severity: 'all',
};

/** True when the ACTIVE section is filtered off its default — drives the dot on the icon. */
export function isFiltered(section: MedicalSection, f: MedicalFilters): boolean {
  if (section === 'medications') return f.medScope !== DEFAULT_FILTERS.medScope;
  if (section === 'questions') return f.qScope !== DEFAULT_FILTERS.qScope;
  return f.symScope !== DEFAULT_FILTERS.symScope || f.severity !== DEFAULT_FILTERS.severity;
}

// ── Toolbar search (client-side, per section) ─────────────────────────────
//
// The magnifier in the Health toolbar filters the ACTIVE section's records by
// text. Fields are the ones a parent would type from memory — names, purpose,
// prescriber, the question, the answer, the description, what helped — never
// ids or enum keys.

function hay(...parts: (string | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function norm(query: string): string {
  return query.trim().toLowerCase();
}

export function medicationMatches(m: Medication, query: string): boolean {
  const q = norm(query);
  if (!q) return true;
  return hay(m.medication_name, m.generic_name, m.purpose, m.prescribing_doctor, m.dose, m.notes).includes(q);
}

export function questionMatches(qn: DoctorQuestion, query: string): boolean {
  const q = norm(query);
  if (!q) return true;
  return hay(qn.question, qn.answer_notes).includes(q);
}

export function symptomMatches(s: SymptomEntry, query: string, linkedMedicationName?: string): boolean {
  const q = norm(query);
  if (!q) return true;
  return hay(s.description, s.what_helped, s.doctor_response, linkedMedicationName).includes(q);
}

// ── Counts (for header pips + section-nav alert dots) ─────────────────────

export interface MedicalCounts {
  upcomingAppts: number;
  prepSoon: number;
  activeMeds: number;
  refillSoon: number;
  pendingQuestions: number;
  forNextVisit: number;
  totalSymptoms: number;
  severeSymptoms: number;
}

// ── Dose timeline (#847 detail view, consuming the #846 dose API) ─────────
//
// The platform stores only what someone asserted: `taken` / `skipped` events
// keyed by (medication_id, date, slot). Everything else on the timeline is
// DERIVED from the absence of a record (D-A) — `pending` until 2h past the
// slot hour, `missed` after that — so the timeline never claims a dose was
// taken unless a person said so. `missed` renders amber (warn), never red
// (D-I): a missed home dose is routine treatment reality, not an emergency.

/** Slot → nominal hour. Mirrors calendar-display.ts SLOT_HOURS (the calendar
 *  mints the same `med-${id}-${date}-${slot}` identity); keep in sync. */
export const MED_SLOT_HOURS: Record<'morning' | 'midday' | 'evening' | 'bedtime', number> = {
  morning: 8,
  midday:  12,
  evening: 18,
  bedtime: 22,
};

/** Grace window after the slot hour before an unlogged dose reads "Missed". */
const MISSED_GRACE_HOURS = 2;

export type DoseTimelineStatus = 'taken' | 'skipped' | 'missed' | 'pending';

export const DOSE_STATUS_LABEL_KEY = {
  taken:   'health:doseStatus.taken',
  skipped: 'health:doseStatus.skipped',
  missed:  'health:doseStatus.missed',
  pending: 'health:doseStatus.pending',
} as const satisfies Record<DoseTimelineStatus, string>;

export const ALL_DOSE_STATUSES: readonly DoseTimelineStatus[] = ['taken', 'skipped', 'missed', 'pending'];

export interface DoseTimelineEntry {
  /** YYYY-MM-DD */
  date: string;
  slot: 'morning' | 'midday' | 'evening' | 'bedtime';
  status: DoseTimelineStatus;
  /** Present only for stored (taken/skipped) events. */
  note?: string;
  logged_at?: string;
}

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Build the dose timeline for one medication over the last `days` calendar
 * days (inclusive of today), newest first.
 *
 * Scheduled meds (a daily frequency with time_of_day slots) get one row per
 * day × slot: the stored event if one exists, otherwise a derived
 * pending/missed row. As-needed and weekly meds have no slot schedule to
 * derive absence against, so they show only the doses actually logged —
 * deriving "missed" for a schedule the platform doesn't hold would be
 * exactly the fabrication this wave removes.
 */
export function buildDoseTimeline(
  med: Medication,
  doses: MedicationDoseEvent[],
  opts: { days?: number; now?: Date } = {},
): DoseTimelineEntry[] {
  const { days = 14, now = new Date() } = opts;
  const own = doses.filter((d) => d.medication_id === med._id);
  const byKey = new Map(own.map((d) => [`${d.date}|${d.slot}`, d]));
  const todayKey = localDateKey(now);

  const hasSchedule =
    med.frequency !== 'as_needed' &&
    med.frequency !== 'weekly' &&
    (med.time_of_day?.length ?? 0) > 0;

  const out: DoseTimelineEntry[] = [];

  if (hasSchedule) {
    for (let i = 0; i < days; i++) {
      const day = new Date(now);
      day.setDate(day.getDate() - i);
      const dateKey = localDateKey(day);
      // Respect the medication's own start/end window — no derived rows for
      // days the schedule didn't exist.
      if (med.start_date && dateKey < med.start_date.slice(0, 10)) continue;
      if (med.end_date && dateKey > med.end_date.slice(0, 10)) continue;
      for (const slot of med.time_of_day) {
        const stored = byKey.get(`${dateKey}|${slot}`);
        if (stored) {
          out.push({ date: dateKey, slot, status: stored.status, note: stored.note || undefined, logged_at: stored.logged_at });
          continue;
        }
        let status: DoseTimelineStatus;
        if (dateKey < todayKey) {
          status = 'missed';
        } else {
          // Today: pending until MISSED_GRACE_HOURS past the slot hour.
          status = now.getHours() >= MED_SLOT_HOURS[slot] + MISSED_GRACE_HOURS ? 'missed' : 'pending';
        }
        out.push({ date: dateKey, slot, status });
      }
    }
  } else {
    // Unscheduled: stored events only, inside the window.
    const start = new Date(now);
    start.setDate(start.getDate() - (days - 1));
    const startKey = localDateKey(start);
    for (const d of own) {
      if (d.date < startKey || d.date > todayKey) continue;
      out.push({ date: d.date, slot: d.slot, status: d.status, note: d.note || undefined, logged_at: d.logged_at });
    }
  }

  const slotOrder = { morning: 0, midday: 1, evening: 2, bedtime: 3 } as const;
  return out.sort((a, b) =>
    a.date === b.date ? slotOrder[b.slot] - slotOrder[a.slot] : b.date.localeCompare(a.date),
  );
}

// ── Symptom CSV export (#847) ─────────────────────────────────────────────
//
// The export audience is an oncologist reading a parent's printout — human
// column headers and plain values, not raw JSON. The backend export shape
// (SymptomEntry.exportLog) returns exactly the fields below; the one id it
// carries (`linked_medication_id`) is never printed raw — it is resolved to
// the medication's name from the caller-supplied meds list, blank when the
// entry has no link or the medication is gone.

export interface SymptomExportRow {
  date: string | Date;
  type: SymptomEntry['type'];
  description: string;
  severity: SymptomEntry['severity'];
  /** ObjectId string or null — resolved to a name client-side, never printed raw. */
  linked_medication_id?: string | null;
  what_helped?: string;
  reported_to_doctor?: boolean;
  doctor_response?: string;
}

// CSV cells go through the SHARED `lib/csvCell` (#1307 / guardrail 16). The
// local `csvField` this replaced did RFC-4180 quoting only, which spreadsheets
// strip BEFORE evaluating a cell whose first character is `= + - @ \t \r` — and
// every column here is family-typed free text.

/**
 * The CSV follows the reader's UI language (R3-B19; `(?)`
 * `symptom-csv-language` sits beside the Export tool): headers, type,
 * severity, Yes/No and the date. `t` defaults to the active language; the
 * page passes its own. The family's text is exported as written.
 */
export function symptomsToCsv(
  rows: SymptomExportRow[],
  medications: Pick<Medication, '_id' | 'medication_name'>[] = [],
  t: AnyTFunction = i18n.t.bind(i18n) as AnyTFunction,
): string {
  const medNameById = new Map(medications.map((m) => [m._id, m.medication_name]));
  // An exported file is data the family keeps, so the non-production machine
  // mark (R3-B16) never enters it (#2109 R3 QA B-D).
  const tt = (key: string): string =>
    (t as unknown as (k: string, o: { postProcess: string[] }) => string)(key, { postProcess: [] });
  const header = [
    tt('health:csv.header.date'),
    tt('health:csv.header.type'),
    tt('health:csv.header.severity'),
    tt('health:csv.header.description'),
    tt('health:csv.header.linkedMedication'),
    tt('health:csv.header.whatHelped'),
    tt('health:csv.header.reportedToCareTeam'),
    tt('health:csv.header.doctorResponse'),
  ].map(csvCell);
  const lines = rows.map((r) => {
    // Date-only (#1308): the CSV must show the day the parent typed, not that
    // day's UTC midnight rendered in whatever zone the browser happens to be.
    const dateLabel = dateOnlyIntl(r.date, 'monthDayYear', String(r.date));
    return [
      dateLabel,
      labelFor(SYMPTOM_TYPE_LABEL_KEY, r.type, t),
      labelFor(SYMPTOM_SEVERITY_LABEL_KEY, r.severity, t),
      r.description ?? '',
      // Blank when unlinked, and blank (not a raw id) when the linked
      // medication no longer exists in the family's list.
      (r.linked_medication_id && medNameById.get(r.linked_medication_id)) || '',
      r.what_helped ?? '',
      r.reported_to_doctor ? tt('common:answers.yes') : tt('common:answers.no'),
      r.doctor_response ?? '',
    ].map(csvCell).join(',');
  });
  return [header.join(','), ...lines].join('\r\n');
}

export function computeCounts(args: {
  appointments: MedicalAppointment[];
  medications: Medication[];
  questions: DoctorQuestion[];
  symptoms: SymptomEntry[];
  now?: Date;
}): MedicalCounts {
  const { appointments, medications, questions, symptoms, now = new Date() } = args;
  return {
    upcomingAppts:    appointments.filter(isUpcomingAppt).length,
    prepSoon:         appointments.filter((a) => isPrepSoon(a, now)).length,
    activeMeds:       medications.filter((m) => m.active).length,
    refillSoon:       medications.filter((m) => m.active && isRefillSoon(m, now)).length,
    pendingQuestions: questions.filter((q) => !q.answered).length,
    forNextVisit:     questions.filter((q) => !q.answered && q.priority === 'this_visit').length,
    totalSymptoms:    symptoms.length,
    severeSymptoms:   symptoms.filter((s) => s.severity === 'severe').length,
  };
}
